<template>
  <div>

    <div v-if="total === 0" class="alert alert-warning text-center">
      There are no claims in the list, so there's nothing to update.
      If you have applied filters, please adjust or clear them to display claims in the list.
    </div>

    <div v-else class="row">
      <div class="col-md-6">
        <h3 class="text-center">The fields to update:</h3>
        <div class="mb-3">
          <button type="button" class="btn btn-sm mr-1 mb-1" :class="{'btn-warning': selected(field), 'btn-info': !selected(field)}"
                  v-for="field in fields" @click="toggle(field)">
            <i v-if="!selected(field)" class="fa fa-plus-circle"></i>
            <i v-if="selected(field)" class="fa fa-minus-circle"></i>
            {{ field.title }}
          </button>
        </div>

        <h3 class="text-center">The method for updating:</h3>
        <div class="mb-3">
          <select name="update_method" v-model="updateMethod" class="form-control">
            <option v-for="method in updateMethods" :value="method.value">{{ method.text }}</option>
          </select>
        </div>

        <div v-if="selectedFields.length" class="alert alert-warning mb-3">
          <div class="mb-2">
            {{ methodDescription }}
          </div>

          <div class="mb-2">
            Please ensure the following:
            <ul>
              <li>the values entered are accurate</li>
              <li>the selected claim list matches what you intend to update</li>
            </ul>

            Once you click the "Apply" button, all claims currently selected in the list below
            <span v-if="pages">(including those on other pages)</span> will be updated.
          </div>

          <div class="text-center">
            <strong>Number of claims to be updated: {{ total }}.<br>This action cannot be undone.</strong>
          </div>
        </div>
      </div>

      <div class="col-md-6">

        <div v-for="field in selectedFields" class="field-area mb-3">
          <h3 class="text-center">{{ field.title }}</h3>
          <div v-html="field.form"></div>
        </div>

        <div v-if="!selectedFields.length" class="alert alert-info text-center">
          Please select the fields and choose the method for bulk updating all the claims ({{ total }}) listed (or filtered) below.
        </div>

      </div>
    </div>

    <input type="hidden" name="fields" v-model="selectedFieldNames">
  </div>
</template>

<script>
import cims from "../../cims";
import { mapActions } from 'pinia'
import { useCitizensTrackingStore } from "../../stores/citizensTracking"

export default {

  props: {
    fields: {type: Array, required: true},
    total: {type: Number, required: true},
    pages: {type: Boolean, required: true},
    initialSelected: {type: Array, default: function () { return [] }},
    initialMethod: {type: String, default: 'complete'},
  },

  data() {
    return {
      selectedFields: [],
      updateMethod: 'complete',
      updateMethods: [
        {
          'value': 'complete',
          'text': 'Complete only the missing values',
          'description': 'Only blank fields will be updated. Fields that are already filled will be disregarded. Additionally, if you don\'t provide a value for a field (where applicable), that field will be ignored.'
        },
        {
          'value': 'overwrite',
          'text': 'Overwrite any original value',
          'description': 'All specified fields will be updated. Both blank and filled fields will be overwritten with new values. If you don\'t provide a value for a field (where applicable), it will be set to an empty value.'
        }
      ],
    }
  },

  methods: {

    toggle(field) {
      if (this.selected(field)) {
        this.selectedFields = this.selectedFields.filter(f => f.name !== field.name);
        return;
      }
      this.selectedFields.push(field);
      this.$nextTick(() => {
        cims.addDataDependentFields();
        cims.addDatePickers();
        cims.addDatePickersNoCurrent();
        cims.addDateTimePickers();
        cims.addDateTimePickersNoCurrent();
      });
    },

    selected(field) {
      return this.selectedFields.find(f => f.name === field.name);
    },

    ...mapActions(useCitizensTrackingStore, ['enableBulkApply', 'disableBulkApply']),

  },

  computed: {

    selectedFieldNames() {
      return JSON.stringify(this.selectedFields.map(f => f.name));
    },

    methodDescription() {
      return this.updateMethods.find(m => m.value === this.updateMethod).description;
    }

  },

  watch: {
    selectedFields: {
      handler(val) {
        if (this.total === 0) return;
        if (val.length > 0) {
          this.enableBulkApply();
        } else {
          this.disableBulkApply();
        }
      },
      deep: true
    },
  },

  mounted() {
    this.updateMethod = this.initialMethod;
    if (this.initialSelected.length > 0) {
      this.fields.forEach(f => {
        let inSelected = this.initialSelected.find(is => is === f.name);
        if (inSelected) {
          this.toggle(f);
        }
      });
    }
  }
}
</script>

<style scoped>
.field-area {
  border: none;
  border-radius: 10px;
  padding: 14px;
  box-shadow: rgba(50, 50, 93, 0.25) 0 2px 5px -1px, rgba(0, 0, 0, 0.3) 0 1px 3px -1px;
}
</style>
